import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { CanAccess, useMenu, useTranslation } from "@refinedev/core";
import { TreeMenuItem } from "@refinedev/core/dist/hooks/menu/useMenu";
import { Dispatch, ReactNode, SetStateAction, useState } from "react";
import { Collapse } from "react-collapse";
import { Link, useLocation } from "react-router-dom";
import { UserType } from "interfaces";
import {
  canViewWpsMenuItem,
  canViewWpsSubMenuItem,
  wpsMenuData,
  WpsDashboardPermission,
  WpsMenuItem,
} from "config/wpsMenu";

interface SubMenuProps {
  id: string;
  name?: string;
  label?: string;
  children: ReactNode;
  icon?: ReactNode;
  parentOpenKey: string;
  setParentOpenKey: Dispatch<SetStateAction<string>>;
  defaultOpen?: boolean;
}

const SubMenu = ({children, name, label, id, icon, parentOpenKey, setParentOpenKey, defaultOpen}: SubMenuProps) => {
  const { defaultOpenKeys } = useMenu();

  const [isOpened, setIsOpened] = useState(defaultOpen ?? defaultOpenKeys.includes(id))

  // useEffect(() => {
  //   if (defaultOpenKeys.includes(id)) {
  //     setParentOpenKey(id);
  //     // setParentOpenKeys([...parentOpenKeys, id]);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const _handleToggleMenu = () => {
    // setParentOpenKey(prevOpenKey => prevOpenKey === id ? "" : id);
    setIsOpened(!isOpened);
  }

  return (
    <li key={id} className="menu-item">
      <button 
        className="w-full flex justify-between items-center text-sm rounded-lg px-3 py-2 leading-6 font-semibold text-neutral-800 hover:bg-magnolia-500" 
        onClick={_handleToggleMenu}
      >
        <span className="flex">
          {icon} {name ?? label}
        </span>
        {isOpened 
          ? <ChevronDownIcon className="w-4 h-4 text-brand-500" /> 
          : <ChevronUpIcon className="w-4 h-4 text-brand-500" />}
      </button>
      <Collapse isOpened={isOpened}>
        <ul className="menu-list flex flex-col gap-y-2 py-2 pl-4 pr-2">{children}</ul>
      </Collapse>
    </li>
  )
}

interface SidebarProps {
  open: boolean;
  permissions?: WpsDashboardPermission[] | null;
  type?: UserType | string;
}

export const Sidebar = ({open, permissions, type = UserType.ADMIN} : SidebarProps) => {
  const { menuItems, selectedKey } = useMenu();
  const { translate } = useTranslation();
  const location = useLocation();
  const isWps = type === UserType.WPS;
  const logoSrc = isWps ? "/images/logo/logo-wps.png" : "/images/logo/colored.png";
  const logoClassName = isWps ? "h-[44px] w-auto object-cover" : "w-24";
  const logoAlt = isWps ? "Actiup WPS Logo" : "Actiup Logo";

  const [parentOpenKey, setParentOpenKey] = useState<string>("");

  const getWpsMenuLabel = (item: WpsMenuItem) => {
    if (item.labelKey) {
      return translate(item.labelKey);
    }

    return item.label ?? item.id;
  };

  const isWpsItemSelected = (item: WpsMenuItem) => {
    if (item.id === "dashboard") {
      return location.pathname.startsWith("/dashboard");
    }

    return !!item.url && location.pathname.includes(item.url);
  };
  
  const renderMenuItems = (items: TreeMenuItem[]) => {
    return items.map(({ key, name, label, icon, route, children, list, identifier }) => {

      if (children.length > 0) {
        return (
          <SubMenu 
            key={key} 
            id={key} 
            name={name} 
            label={label} 
            icon={icon}
            parentOpenKey={parentOpenKey}
            setParentOpenKey={setParentOpenKey}
          >
            {renderMenuItems(children)}
          </SubMenu>
        );
      }

      const isSelected = key === selectedKey;

      return (
        <CanAccess key={name} resource={identifier ?? name} action="list">
          <li key={key}>
            <Link  
              to={route ?? "/"}
              className={`flex rounded-lg px-3 py-2 text-sm leading-6 font-semibold text-neutral-800 ${isSelected ? "bg-magnolia-500" : "hover:bg-magnolia-500"}`}
            >
              {icon} {name}
            </Link>
          </li>
        </CanAccess>
      );
    })
  };

  const renderWpsMenuItems = (items: WpsMenuItem[]) => {
    return items.map((menuItem) => {
      if (!canViewWpsMenuItem(menuItem, permissions ?? [])) {
        return null;
      }

      if (menuItem.subMenu && menuItem.subMenu.length > 0) {
        const permittedSubMenuItems = menuItem.subMenu.filter((item) =>
          canViewWpsSubMenuItem(menuItem.id, item, permissions ?? [])
        );

        if (permittedSubMenuItems.length === 0) {
          return null;
        }

        return (
          <SubMenu
            key={menuItem.id}
            id={menuItem.id}
            name={getWpsMenuLabel(menuItem)}
            icon={menuItem.icon as ReactNode}
            parentOpenKey={parentOpenKey}
            setParentOpenKey={setParentOpenKey}
            defaultOpen={permittedSubMenuItems.some(isWpsItemSelected)}
          >
            {permittedSubMenuItems.map((item) => {
              const isSelected = isWpsItemSelected(item);

              return (
                <li key={`${menuItem.id}-${item.id}`}>
                  <Link
                    to={item.url}
                    className={`flex rounded-lg px-3 py-2 text-sm leading-6 font-semibold text-neutral-800 ${isSelected ? "bg-magnolia-500" : "hover:bg-magnolia-500"}`}
                  >
                    {item.icon} {getWpsMenuLabel(item)}
                  </Link>
                </li>
              );
            })}
          </SubMenu>
        );
      }

      const isSelected = isWpsItemSelected(menuItem);

      return (
        <li key={menuItem.id}>
          <Link
            to={menuItem.url}
            className={`flex rounded-lg px-3 py-2 text-sm leading-6 font-semibold text-neutral-800 ${isSelected ? "bg-magnolia-500" : "hover:bg-magnolia-500"}`}
          >
            {menuItem.icon} {getWpsMenuLabel(menuItem)}
          </Link>
        </li>
      );
    });
  };

  return (
    <div className={`hidden lg:transition-all lg:duration-300 lg:fixed lg:inset-y-0 lg:z-50 lg:flex ${open ? "ml-0" : "-ml-72"} lg:w-72 lg:flex-col lg:shadow-lg`}>
      {/* Sidebar */}
      <div className="p-4 bg-white flex shrink-0 items-center min-h-16">
        <Link to="/dashboard/overview">
          <img
            src={logoSrc}
            alt={logoAlt}
            className={logoClassName}
          />
        </Link>
      </div>

      <div className="scrollbar-hidden w-full h-full overflow-y-auto bg-white px-4 pb-4">
        <ul className="flex flex-1 flex-col gap-y-2 mt-3">
          {isWps ? renderWpsMenuItems(wpsMenuData) : renderMenuItems(menuItems)}
        </ul>
      </div>
    </div>
  ) 
};
